import dayjs from 'dayjs';
import { MapUtils } from '../../../utility/map-utils.js';
import {
  DataModel, DataModelBuilder, SerializationContext, SerializedData,
} from '../../dto/data-model.js';
import {
  cloneListOfCloneable, isSameData, isSameList,
} from '../../dto/data-model-utils.js';
import {
  IEventOpenAccessProtocol, OpenAccessProtocol, OpenAccessProtocolType,
} from './open-access-protocol.js';
import { JSONUtils } from '../../../utility/json-utils.js';
import { Cardinal } from '../../core/cardinal.js';
import { MigrationSafe } from '../../core/migration-safe.js';
import { BooleanOrListOfString } from '../../core/bool-or-list.js';
import { Stage } from './stage.js';
import { PassEventValidityRule } from './open-access-pass-template.js';
import { fromEnumAsString, toEnumFromString } from '../../../index.js';

export class StagedSeatedOpenAccessProtocol extends OpenAccessProtocol implements IEventOpenAccessProtocol {
  static readonly TYPE_ID: string = '4b4b2c9226d84968a7c7f91854820878';

  public readonly type: OpenAccessProtocolType = OpenAccessProtocolType.StagedPerSeat;

  private constructor(
      public readonly eventValidityRule: PassEventValidityRule,
      public readonly maxEventCount: MigrationSafe<Cardinal>,
      public readonly startDate: dayjs.Dayjs,
      public readonly expirationDate: dayjs.Dayjs,

      public readonly stages: Stage[],
  ) {
    super();
  }

  private static create(args: {
    eventValidityRule: PassEventValidityRule
    maxEventCount: MigrationSafe<Cardinal>,
    startDate: dayjs.Dayjs,
    expirationDate: dayjs.Dayjs,
    stages: Stage[],
  }): StagedSeatedOpenAccessProtocol {
    return new StagedSeatedOpenAccessProtocol(
        args.eventValidityRule,
        args.maxEventCount,
        args.startDate,
        args.expirationDate,
        args.stages,
    );
  }

  static forTemplate(args: {
    eventValidityRule: PassEventValidityRule,
    startDate: dayjs.Dayjs,
    expirationDate: dayjs.Dayjs,
    stages: Stage[],
  }): StagedSeatedOpenAccessProtocol {
    return this.create({
      ...args,
      maxEventCount: MigrationSafe.some(Cardinal.finite(0)),
    });
  }

  static forInstance(args: {
    eventValidityRule: PassEventValidityRule,
    maxEventCount: Cardinal,
    startDate: dayjs.Dayjs,
    expirationDate: dayjs.Dayjs,
    stages: Stage[],
  }): StagedSeatedOpenAccessProtocol {
    return this.create({
      ...args,
      maxEventCount: MigrationSafe.some(args.maxEventCount.clone()),
    });
  }

  get $typeId(): string {
    return StagedSeatedOpenAccessProtocol.TYPE_ID;
  }

  static fromMap(context: SerializationContext, map: SerializedData): StagedSeatedOpenAccessProtocol {
    return new StagedSeatedOpenAccessProtocol(
        toEnumFromString(MapUtils.getString(map, 'eventValidityRule'), PassEventValidityRule),
        MigrationSafe.decode(
            MapUtils.getRaw(map, 'maxEventCount'),
            (data) => Cardinal.decode(JSONUtils.decodeNumber(data)),
        ),
        context.decodeDate(MapUtils.getNumber(map, 'startDate')),
        context.decodeDate(MapUtils.getNumber(map, 'expirationDate')),
        MapUtils.getList<Stage>(map, 'stages', (value) => context.fromData<Stage>(JSONUtils.decodeRecord(value))),
    );
  }

  toMap(context: SerializationContext):SerializedData {
    return {
      'type': this.type,
      'eventValidityRule': fromEnumAsString(this.eventValidityRule),
      'maxEventCount': this.maxEventCount.encode((value) => value.encode()),
      'startDate': context.encodeDate(this.startDate),
      'expirationDate': context.encodeDate(this.expirationDate),
      'stages': this.stages.map((stage) => context.toData(stage)),
    };
  }

  clone(): this {
    return this.copyWith();
  }

  applyTemplateChanges(other: this): this {
    return this.copyWith({
      maxEventCount: this.maxEventCount.clone((value) => value.clone()),
      startDate: this.startDate,
      expirationDate: this.expirationDate,
      stages: this.stages.map((stage) => {
        const templateStage = other.stages.find((candidate) => candidate.uniqueId === stage.uniqueId);

        return stage.copyWith({
          entries: stage.entries.map((entry) => {
            const templateEntry = templateStage?.entries.find((candidate) => candidate.uniqueId === entry.uniqueId);

            return templateEntry == null ? entry.clone() : entry.copyWith({
              name: templateEntry.name,
              eventConfiguration: templateEntry.cloneEventConfiguration(),
            });
          }),
        });
      }),
    });
  }

  copyWith({
    eventValidityRule = this.eventValidityRule,
    maxEventCount = this.maxEventCount.clone((value) => value.clone()),
    startDate = this.startDate,
    expirationDate = this.expirationDate,
    stages = cloneListOfCloneable(this.stages),
  }: {
    eventValidityRule?: PassEventValidityRule,
    maxEventCount?: MigrationSafe<Cardinal>,
    startDate?: dayjs.Dayjs,
    expirationDate?: dayjs.Dayjs,
    stages?: Stage[],
  } = {}): this {
    return StagedSeatedOpenAccessProtocol.create({
      eventValidityRule,
      maxEventCount,
      startDate,
      expirationDate,
      stages,
    }) as this;
  }

  isSame(other: StagedSeatedOpenAccessProtocol): boolean {
    if(other === this) {
      return true;
    }

    return other.maxEventCount.isSame(this.maxEventCount, (other, self) => other.isSame(self)) &&
        isSameList(other.stages, this.stages, isSameData) &&
        other.eventValidityRule === this.eventValidityRule &&
        other.startDate.isSame(this.startDate) &&
        other.expirationDate.isSame(this.expirationDate);
  }

  /**
 * @deprecated Event configuration is stored in the stage entries of the protocol. Use their `cloneEventConfiguration()` instead.
 */
  cloneEventConfiguration(): Map<string, BooleanOrListOfString> {
    const eventConfiguration = new Map<string, BooleanOrListOfString>();
    for(const stage of this.stages) {
      for(const entry of stage.entries) {
        for(const [eventId, configuration] of entry.eventConfiguration) {
          const existingConfiguration = eventConfiguration.get(eventId);
          if(existingConfiguration == null) {
            eventConfiguration.set(eventId, configuration.clone());
          }
        }
      }
    }

    return eventConfiguration;
  }

  isEventAssignedToPass({ eventId, passVariationId }: {
    eventId: string,
    passVariationId: string,
  }): boolean {
    return this.stages.some((stage) => stage.entries.some((entry) =>
      entry.isEventAssignedToPass({ eventId, passVariationId, eventValidityRule: this.eventValidityRule })));
  }

  isEventAssignedToTemplate({ eventId }: { eventId: string }): boolean {
    return this.stages.some((stage) => stage.entries.some((entry) =>
      entry.isEventAssignedToTemplate({ eventId, eventValidityRule: this.eventValidityRule })));
  }

  getAssignedEventIdsForPass({ passVariationId }: { passVariationId: string }): string[] {
    const assignedEventIds = new Set<string>();
    for(const stage of this.stages) {
      for(const entry of stage.entries) {
        for(const eventId of entry.getAssignedEventIdsForPass({ passVariationId, eventValidityRule: this.eventValidityRule })) {
          assignedEventIds.add(eventId);
        }
      }
    }

    return [...assignedEventIds];
  }

  static verifyType(dm: DataModel): boolean {
    return dm instanceof StagedSeatedOpenAccessProtocol;
  }

  static assertType(dm: DataModel): dm is StagedSeatedOpenAccessProtocol {
    return dm instanceof StagedSeatedOpenAccessProtocol;
  }

  static convertToLegacyEventList(protocol: StagedSeatedOpenAccessProtocol): string[] | null {
    return IEventOpenAccessProtocol.convertToLegacyEventList(protocol.cloneEventConfiguration());
  }
}

export class StagedSeatedOpenAccessProtocolBuilder extends DataModelBuilder<StagedSeatedOpenAccessProtocol> {
  build(context: SerializationContext, map: SerializedData): StagedSeatedOpenAccessProtocol {
    return StagedSeatedOpenAccessProtocol.fromMap(context, map);
  }
}
